import { render, Dynamic } from 'solid-js/web';
import { Show, createEffect } from 'solid-js';
import Nav from '~/features/nav';
import SignIn from '~/features/sign-in';
import { initializeTheme } from '~/stores/theme-store';
import { initializeNav, page } from '~/stores/nav-store';
import { initializeAuth, checked, signedIn } from '~/stores/auth-store';
import { pageById } from '~/pages/registry';
import { load, realPortfolio, selectedPlan, setActiveId } from '~/stores/portfolio-store';
import { loadTransactions } from '~/stores/transactions-store';
import { load as loadProfile } from '~/stores/profile-store';
import { syncUrl as syncCompareUrl } from '~/stores/compare-store';
import { syncUrl as syncExploreUrl } from '~/stores/explore-store';
import './app.css';

const Root = () => {
    initializeTheme();
    initializeNav();
    initializeAuth();

    // Which book the panes read is a function of which page you are on, not a
    // mode you put the app into. Accounts, Loans, Payments, Explore and Project
    // are always the real book; Plan is whichever plan that page has selected;
    // Nothing to toggle, nothing that stays switched
    // after you navigate away — leaving the page is what puts it back.
    createEffect(() => {
        const target = page() === 'plan' ? selectedPlan() : realPortfolio();
        if (target) setActiveId(target.id);
    });
    // Positions are summed from the blotter, so both have to land before the
    // accounts pane shows anything real. Loaded in the shell rather than per
    // page: the header's totals need them on every page, and a fetch per
    // navigation would make switching pages feel like a page load.
    //
    // Keyed to the session rather than to mount, so signing in loads the book
    // without a reload — and so a signed-out visitor never fires a query that
    // could only ever come back empty.
    // Inside the render, so the effects have an owner and are flushed from the
    // first paint — at module scope the first selection change silently did not
    // reach the address bar.
    syncExploreUrl();
    syncCompareUrl();

    createEffect(() => {
        if (!signedIn()) return;
        void load();
        void loadTransactions();
        void loadProfile();
    });

    // Nothing of the app renders without a session — not the nav, not the
    // header's totals. Those totals are the reason: signed out they are a
    // confident $0.00, which is a statement about the portfolio rather than
    // about the session, and it is the wrong one.
    //
    // The `checked()` gate holds a blank surface rather than the sign-in panel
    // while the stored session resolves, so a signed-in reload does not flash
    // "sign in" on its way to the book.
    return (
        <Show when={checked()} fallback={<div class='h-screen bg-background' />}>
            <Show
                when={signedIn()}
                fallback={
                    <div class='h-screen bg-border p-0 panels-flush'>
                        <SignIn />
                    </div>
                }>
                <AppShell />
            </Show>
        </Show>
    );
};

// Flush framing: no window padding, panes tiled edge-to-edge over the border
// colour with 1px seams, square corners (the `.panels-flush` rule in app.css).
// text-foreground goes on the root rather than on <main>, since the nav sits
// outside main and anything there without its own tone would otherwise fall
// back to the browser default — black on black in the dark theme.
//
// Nav across the top, the page's panes filling everything under it. No header
// here: the running totals are the book's totals, so they belong to the Accounts
// page and are rendered by it. Each page owns its internal layout; all main
// provides is the flush framing and a row to lay them out in.
const AppShell = () => {
    return (
        <div class='h-screen flex flex-col gap-px bg-border text-foreground'>
            <Nav />
            <main class='p-0 gap-px panels-flush flex-1 min-h-0 flex bg-border'>
                <Dynamic component={pageById(page()).component} />
            </main>
        </div>
    );
};

const rootEl = document.getElementById('root')!;

// Under HMR, vite-plugin-solid re-executes this module's top-level code on each
// save, which calls render() again and appends a *second* <Root /> tree into
// #root. solid-refresh installs its own import.meta.hot.accept here, so a plain
// hot.dispose() handler doesn't reliably fire. Instead, stash the dispose fn on
// hot.data (which survives across this module's HMR updates) and tear down the
// previous tree at the top of every re-execution before mounting a fresh one.
if (import.meta.hot?.data?.dispose) {
    import.meta.hot.data.dispose();
    rootEl.innerHTML = '';
}

const dispose = render(() => <Root />, rootEl);

if (import.meta.hot) {
    import.meta.hot.data.dispose = dispose;
}
